import { AgGridReact } from 'ag-grid-react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import { InitialFetchStateProvider } from '../AgGrid/AgGridLoadingOverlay';
import { useAgGridPopupClosing } from '../AgGrid/useAgGridPopupClosing';
import { BlotterTableWrapper } from './styles';
import type { BlotterTableProps } from './types';

import type { BodyScrollEvent, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { noop } from 'lodash-es';
import { type KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { useCallbackRef } from '../../hooks';
import { useCurrentDocument } from '../../hooks/useCurrentDocument';
import { useIntlContext } from '../../hooks/useIntlContext';
import { cypressGridSettings } from '../../utils/envCypress';
import { BlotterTableFormattedNumberProvider } from './BlotterTableFormattedNumberProvider';
import { useBlotterComponents } from './useBlotterComponents';
import { BlotterErrorDetail } from './useBlotterTable/ErrorDetail';
import { defaultGetQuickFilterText, getAgGridLocaleText } from './utils';

// Important that this is stable
export const DEFAULT_BLOTTER_COLDEF: ColDef = {
  resizable: true,
  cellDataType: false,
  filterParams: {
    buttons: ['reset', 'apply'],
  },
  getQuickFilterText: defaultGetQuickFilterText,
};

/**
 * Desktop/tablet blotter table implementation based on AG Grid.
 * Mobile consumers should use {@link MobileDataTable} instead.
 */
export function BlotterTable({
  gridOptions,
  density,
  background,
  headerBackground,
  hideHeaderBorder,
  extraComponents,
  hidePopupMenu,
  hidden,
  rowEndSpacing,
  errorDetails,
  initialFetchState,
  settings,
  dialogShowJsonModal = null,
}: BlotterTableProps) {
  const { locale } = useIntlContext();
  const [pinnedLeftOverlapping, setPinnedLeftOverlapping] = useState(false);
  const handleBodyScroll = useCallback((e: BodyScrollEvent) => {
    setPinnedLeftOverlapping(e.left > 0);
  }, []);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
  const currentDocument = useCurrentDocument();
  const memoizedGridOptions = useMemo(() => {
    // Determine popupParent based on dialog ancestor
    let popupParent: HTMLElement | undefined;

    if (wrapperRef) {
      const popoutAncestor = wrapperRef.closest('.dv-popout-window');
      const dialogAncestor = wrapperRef.closest('[role="dialog"]');

      popupParent = (dialogAncestor ?? popoutAncestor ?? currentDocument.querySelector('body')) as HTMLElement;
    }

    return {
      suppressRowVirtualisation: cypressGridSettings.isRowVirtSuppressed(),
      suppressColumnVirtualisation: cypressGridSettings.isColVirtSuppressed(),

      ...gridOptions,
      popupParent,
    } satisfies GridOptions;
  }, [currentDocument, gridOptions, wrapperRef]);

  useAgGridPopupClosing(hidePopupMenu ?? noop);

  const components = useBlotterComponents(extraComponents);

  const handleGridReady = useCallbackRef((event: GridReadyEvent) => {
    setGridApi(event.api);
    memoizedGridOptions?.onGridReady?.(event);
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!gridApi) {
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
        gridApi.selectAll('filtered');
      }
    },
    [gridApi]
  );

  return (
    <>
      <InitialFetchStateProvider value={initialFetchState}>
        <BlotterTableFormattedNumberProvider settings={settings}>
          <BlotterTableWrapper
            background={background}
            headerBackground={headerBackground}
            hideHeaderBorder={hideHeaderBorder}
            rowEndSpacing={rowEndSpacing}
            className="ag-theme-balham-dark"
            density={density}
            pinnedLeftOverlapping={pinnedLeftOverlapping}
            data-testid="blotter-table-wrapper"
            hidden={hidden}
            ref={setWrapperRef}
            onKeyDown={handleKeyDown}
          >
            <BlotterErrorDetail
              errorDetails={errorDetails}
              disableErrorBanner={gridOptions?.context?.current?.settings?.disableErrorBanner}
            />
            <AgGridReact
              localeText={getAgGridLocaleText(locale)}
              defaultColDef={DEFAULT_BLOTTER_COLDEF}
              // Native agNoRowsOverlay unless a caller registers extraComponents.noRowsOverlay.
              {...(extraComponents?.noRowsOverlay != null ? { noRowsOverlayComponent: 'noRowsOverlay' } : {})}
              /** Defaulted to true. AgGrid changed the default in v29 from true to false, but we keep it as it was after upgrading. */
              groupAllowUnbalanced={true}
              {...memoizedGridOptions}
              onBodyScroll={handleBodyScroll}
              components={components}
              tooltipShowDelay={300}
              loadingOverlayComponent="loadingOverlay"
              suppressMultiSort={false}
              suppressColumnMoveAnimation={true}
              onGridReady={handleGridReady}
            />
          </BlotterTableWrapper>
        </BlotterTableFormattedNumberProvider>
      </InitialFetchStateProvider>
      {dialogShowJsonModal}
    </>
  );
}
